import React, { useEffect } from 'react';
import { Toaster, ToastBar, toast as reactHotToast } from 'react-hot-toast';

import { flushQueuedToast } from './pendingToast';

interface ToastProviderProps {
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  duration?: number;
}

const ToastProvider: React.FC<ToastProviderProps> = ({ position = 'top-right', duration = 10000 }) => {
  // Fires any toast queued (via queueToast) right before a hard navigation
  // (window.location.href), since that reload starts this component fresh
  // and no in-memory toast call could have survived it.
  useEffect(() => {
    flushQueuedToast();
  }, []);

  return (
    <Toaster
      position={position}
      toastOptions={{
        duration,
        success: {
          iconTheme: {
            primary: 'var(--color-success-500)',
            secondary: 'var(--color-white)',
          },
        },
        error: {
          iconTheme: {
            primary: 'var(--color-error-500)',
            secondary: 'var(--color-white)',
          },
        },
        style: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      {(t) => (
        <div
          onClick={() => reactHotToast.dismiss(t.id)}
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            width: 360,
            maxWidth: 'calc(100vw - 32px)',
            padding: '14px 16px',
            borderRadius: 12,
            background: 'var(--color-gray-light-25)',
            border: '1px solid var(--color-gray-light-200)',
            borderLeft: `4px solid ${
              t.type === 'success'
                ? 'var(--color-success-500)'
                : t.type === 'error'
                  ? 'var(--color-error-500)'
                  : 'var(--color-brand-500)'
            }`,
            boxShadow:
              '0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08)',
            cursor: 'pointer',
            opacity: t.visible ? 1 : 0,
            transition: 'opacity 150ms ease, transform 150ms ease',
            transform: t.visible ? 'translateY(0)' : 'translateY(-8px)',
          }}
        >
          <ToastBar
            toast={{ ...t, style: { background: 'transparent', boxShadow: 'none', padding: 0 } }}
          >
            {({ icon, message }) => (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%' }}>
                <span style={{ marginTop: 1 }}>{icon}</span>
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    color: 'var(--color-gray-light-900)',
                    fontFamily: 'inherit',
                  }}
                >
                  {message}
                </span>
              </div>
            )}
          </ToastBar>
        </div>
      )}
    </Toaster>
  );
};

// Export toast function for convenience
export const toast = reactHotToast;

export default ToastProvider;
